import * as THREE from 'three';
import { resolveTier, TIERS } from '../render/Quality.js';

/**
 * Owns the WebGL context, the frame loop and the quality tier. Systems are
 * plain objects with an optional update(dt, elapsed); the actual draw call is
 * delegated to a render hook so the portal pass can run before post-processing.
 */
export class Engine {
  constructor({ canvas, bus, settings }) {
    this.bus = bus;
    this.settings = settings;
    this.canvas = canvas;

    this.tier = resolveTier(settings.get('quality'));

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,          // post FX chain does its own edge treatment
      powerPreference: 'high-performance',
      stencil: false,
      depth: true,
      alpha: false,
    });
    this.renderer.setClearColor(0x05060c, 1);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.25;
    this.renderer.shadowMap.enabled = this.tier.shadows;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    // Shadow maps are refreshed once per frame by hand: a portal frame issues
    // several scene renders and three would otherwise redraw them every time.
    this.renderer.shadowMap.autoUpdate = false;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(settings.get('fov'), 1, 0.06, 2000);
    this.scene.add(this.camera);

    // Timer.connect gives us the Page Visibility API, so a backgrounded tab
    // does not return a twelve second delta when it wakes up.
    this.timer = new THREE.Timer();
    this.timer.connect(document);
    this.elapsed = 0;
    this.gameTime = 0;   // advances only while not paused; see _tick
    this.frame = 0;
    this.running = false;
    this.paused = false;

    /** @type {Array<{update?:Function, resize?:Function, dispose?:Function, order?:number}>} */
    this.systems = [];
    this.renderHook = null;

    this._frameTimes = new Float32Array(60);
    this._ftIndex = 0;
    this._adaptCooldown = 3;
    this.fps = 60;

    this._onResize = this._onResize.bind(this);
    this._tick = this._tick.bind(this);
    window.addEventListener('resize', this._onResize);
    window.addEventListener('orientationchange', this._onResize);
    this._onResize();
  }

  addSystem(sys) {
    this.systems.push(sys);
    this.systems.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return sys;
  }

  removeSystem(sys) {
    const i = this.systems.indexOf(sys);
    if (i >= 0) this.systems.splice(i, 1);
  }

  applyTier(name) {
    const tier = resolveTier(name);
    if (tier === this.tier) return;
    this.tier = tier;
    this.renderer.shadowMap.enabled = tier.shadows;
    this._onResize();
    for (const s of this.systems) s.onTier?.(tier);
  }

  get pixelRatio() {
    return Math.min(window.devicePixelRatio || 1, this.tier.pixelRatio);
  }

  /**
   * three's `fov` is vertical, so a phone held upright turns a comfortable
   * 88 degrees into about 49 degrees of horizontal view, which feels like
   * playing through a letterbox. Widen the vertical field on tall screens so
   * the horizontal one stays usable, capped before it goes fisheye.
   */
  verticalFovFor(baseFov) {
    const aspect = this.camera.aspect || 1;
    if (aspect >= 1.2) return baseFov;
    const targetHorizontal = THREE.MathUtils.degToRad(76);
    const v = THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(targetHorizontal / 2) / aspect));
    return THREE.MathUtils.clamp(v, baseFov, 104);
  }

  _onResize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.width = w; this.height = h;
    this.renderer.setPixelRatio(this.pixelRatio);
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    for (const s of this.systems) s.resize?.(w, h, this.pixelRatio);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.timer.reset();
    this.renderer.setAnimationLoop(this._tick);
  }

  stop() {
    this.running = false;
    this.renderer.setAnimationLoop(null);
  }

  _tick(timestamp) {
    this.timer.update(timestamp);
    const raw = this.timer.getDelta();
    // A long stall (tab switch, GC pause) must not teleport the player through
    // a wall, so the step is clamped rather than replayed.
    const dt = Math.min(raw, 0.05);
    this.elapsed += dt;
    this.frame++;

    this._trackPerf(raw);

    // Two clocks. `elapsed` is wall-ish and keeps running through a pause
    // (perf sampling, agent waits). `gameTime` only advances while the
    // simulation does, and it is the clock every system is handed: a
    // gameplay lifetime read from the running clock expired portals on
    // the first frame after a pause (audit finding 3, reproduced).
    if (!this.paused) {
      this.gameTime += dt;
      for (const s of this.systems) s.update?.(dt, this.gameTime);
    } else {
      for (const s of this.systems) if (s.updateWhilePaused) s.update?.(0, this.gameTime);
    }

    // One shadow refresh per frame, before any of the portal sub-renders.
    if (this.renderer.shadowMap.enabled) this.renderer.shadowMap.needsUpdate = true;

    if (this.renderHook) this.renderHook(dt, this.elapsed);
    else this.renderer.render(this.scene, this.camera);
  }

  _trackPerf(raw) {
    this._frameTimes[this._ftIndex] = raw;
    this._ftIndex = (this._ftIndex + 1) % this._frameTimes.length;
    if (this._ftIndex !== 0) return;

    let sum = 0;
    for (let i = 0; i < this._frameTimes.length; i++) sum += this._frameTimes[i];
    const avg = sum / this._frameTimes.length;
    this.fps = 1 / Math.max(avg, 1e-4);

    if (this.settings.get('quality') !== 'auto') return;
    this._adaptCooldown -= sum;
    if (this._adaptCooldown > 0) return;

    // Only ever step down automatically. Climbing back up mid-run causes a
    // visible hitch (shader recompiles) which reads worse than a steady 45fps.
    if (avg > 0.024 && this.tier !== TIERS.low) {
      const next = this.tier === TIERS.high ? 'medium' : 'low';
      console.info(`[engine] adaptive quality -> ${next} (avg ${(avg * 1000).toFixed(1)}ms)`);
      this.applyTier(next);
      this._adaptCooldown = 8;
    }
  }

  dispose() {
    this.stop();
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('orientationchange', this._onResize);
    for (const s of this.systems) s.dispose?.();
    this.systems.length = 0;
    this.timer.dispose();
    this.renderer.dispose();
  }
}
